import {
  ORG_SLUG_REGEX,
  RESERVED_ORG_SLUGS
} from '../constants/Validation.constants';
import { AssignablePlatformRole, PlatformRole } from '../types/Team.types';

// Every group this app owns hangs off one namespace group, so a Keycloak realm
// shared with a client's other applications cannot produce a path we mistake for
// ours. The group-membership mapper emits EVERY group a user belongs to, which
// left two failures open under the old two-segment rule:
//   - Lockout: a member who also sat in a foreign `/finance/admin` yielded a
//     second candidate slug and failed the "exactly one (slug, role)" rule — a
//     redirect to /no-organization for doing nothing wrong. A foreign two-segment
//     path is now simply skipped, so they resolve normally.
//   - Escalation: a foreign top-level `/SUPER_ADMIN` group resolved to platform
//     staff for any realm user. Only the namespaced path does now.
// Compared CASE-SENSITIVELY, like the slug: `/CompliAI` and `/compliai` are
// distinct Keycloak groups. Must equal APP_GROUP_NAMESPACE in the API's
// common/authorization/organization-claims.ts.
const APP_NAMESPACE = 'compliai';
const SUPER_ADMIN_PATH = `/${APP_NAMESPACE}/SUPER_ADMIN`;

// Only admin and user exist as organization leaf groups. Keyed on the lowercased
// leaf, rather than uppercase-then-includes, so the rule cannot drift from the
// server's ROLE_BY_LEAF on casing.
const ROLE_BY_LEAF: Record<string, AssignablePlatformRole> = {
  admin: 'ADMIN',
  user: 'USER'
};

export interface PlatformClaims {
  role: PlatformRole | null;
  /** null if and only if role is SUPER_ADMIN, or nothing resolved at all. */
  orgSlug: string | null;
}

interface OrgMembership {
  slug: string;
  role: AssignablePlatformRole;
}

// Trims and drops empties, mirroring the server's toGroupPaths: without the trim
// a padded entry splits into four segments and is silently discarded.
const toPaths = (claim: unknown): string[] =>
  (Array.isArray(claim) ? claim : [claim])
    .filter((path): path is string => typeof path === 'string')
    .map((path) => path.trim())
    .filter((path) => path.length > 0);

// The slug is an identifier now, not a label, so it is validated rather than
// trusted. Deliberately does NOT lowercase, matching the server: Keycloak group
// names are case-sensitive, so folding '/Acme' onto 'acme' would let a
// hand-created group alias onto another organization's tenancy. This is a private
// boolean over the same two constants the creation form uses; the exported
// helpers in Organization.util.ts must not be reused here — deriveOrgSlug
// rewrites characters, and validateOrgSlug returns a human error string.
const isValidOrgSlug = (slug: string): boolean =>
  !RESERVED_ORG_SLUGS.includes(slug) && ORG_SLUG_REGEX.test(slug);

/**
 * Exactly one (slug, role) pair, both read from the SAME three-segment path
 * (namespace/slug/leaf) so the two can never disagree. The namespace check skips
 * any group that is not ours, so a foreign `/finance/admin` never becomes a
 * candidate. More than one distinct pair — two organizations, or one organization
 * granting two roles — resolves to nothing rather than picking one: the claim is
 * unordered, so picking would be nondeterministic across token refreshes, and
 * `org_id` is no longer there to break the tie.
 */
const parseOrgMembership = (paths: string[]): OrgMembership | null => {
  const memberships = new Map<string, OrgMembership>();

  for (const path of paths) {
    const segments = path.split('/').filter(Boolean);
    if (segments.length !== 3) continue;

    const [namespace, slug, leaf] = segments;
    if (namespace !== APP_NAMESPACE) continue;

    const role = ROLE_BY_LEAF[leaf.toLowerCase()];
    if (!role || !isValidOrgSlug(slug)) continue;

    memberships.set(`${slug}/${role}`, { slug, role });
  }

  return memberships.size === 1 ? Array.from(memberships.values())[0] : null;
};

/**
 * `platform_role` is a Keycloak group-membership claim of full group paths and,
 * since `org_id` was removed, the only claim carrying identity. It is
 * multivalued and unordered, so it is never read positionally.
 *
 * Mirrors parseOrgMembershipClaim / resolveActorScope in the API's
 * common/authorization/organization-claims.ts. The two must agree: a slug this
 * side rejects and the server accepts sends a legitimate member to
 * /no-organization with no 401 to recover from, which is the unrecoverable
 * direction.
 */
export const resolvePlatformClaims = (claim: unknown): PlatformClaims => {
  const paths = toPaths(claim);

  // Superadmin wins outright, BEFORE any leaf is inspected. The inverse ordering
  // is not a lockout but a privilege widening: a superadmin who also belongs to
  // an organization would resolve to ADMIN + slug, dropping out of the
  // superAdminAllowedPaths allowlist (ProtectedRoute) and so opening every route,
  // while useAuthorization simultaneously grants full org-admin write permissions.
  if (paths.includes(SUPER_ADMIN_PATH)) {
    return { role: 'SUPER_ADMIN', orgSlug: null };
  }

  const membership = parseOrgMembership(paths);

  return membership
    ? { role: membership.role, orgSlug: membership.slug }
    : { role: null, orgSlug: null };
};
